/**
 * DesktopTransitionLayer — performs the "New Desktop" navigation.
 *
 * Despite the legacy name, this renders nothing. When the desktop transition
 * store is signalled (startTransition), it tears down the outgoing space store
 * and navigates to /desktops/:id in intake mode with the optimistic cachedSpace.
 *
 * Routed through navigateWithDesktopSlide so a new Desktop gets the exact same
 * "slide over a blurred backdrop" treatment as switching between existing
 * Desktops, instead of a separate, more subtle fallback. On browsers without
 * View Transitions support, navigateWithDesktopSlide falls back to a plain
 * navigate() and the incoming DesktopShell slides itself in via the `slideIn`
 * route-state flag (see DesktopShell + the `.desktop-shell-slide-in` rule in
 * App.scss) — there is NO masking overlay either way: the real new desktop
 * renders immediately (the cachedSpace lets SpacePageWrapper skip the loader).
 *
 * Mounted outside the keyed DesktopPageWrapper so this navigator survives the
 * keyed remount it triggers.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDesktopTransitionStore, buildDesktopIntakeNavArgs } from '../../../../stores/desktopTransitionStore';
import { navigateWithDesktopSlide } from '../../../../utils/desktopSlideTransition';
import useSpaceStore from '../../../../stores/spaceStore';

export const DesktopTransitionLayer: React.FC = () => {
  const navigate = useNavigate();
  const phase = useDesktopTransitionStore((s) => s.phase);

  useEffect(() => {
    if (phase !== 'pending') return;

    const { incomingSpaceId, incomingThreadId, incomingTabId, cachedSpace, reset } =
      useDesktopTransitionStore.getState();

    if (!incomingSpaceId) {
      reset();
      return;
    }

    // Tear down the outgoing space's store state BEFORE navigating. The keyed
    // DesktopPageWrapper remounts on the URL change, but spaceStore is a global
    // Zustand store that survives the remount — without this, the fresh mount
    // would paint the previous desktop's windows for a frame before the cleanup
    // effect runs. Clearing it here also guarantees activeSpace is null when the
    // intake desktop mounts, which preserves the startIntake → setActiveDesktop
    // effect ordering.
    useSpaceStore.getState().cleanup();

    const { to, options } = buildDesktopIntakeNavArgs(incomingSpaceId, incomingThreadId!, incomingTabId!, cachedSpace!);
    navigateWithDesktopSlide(navigate, to, options);

    reset();
  }, [phase, navigate]);

  return null;
};

export default DesktopTransitionLayer;
